import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { cn } from '../../utils/cn';
import { Icon } from '../Icon';
import { useSiteContent } from '../../hooks/useSiteContent';
import logoLight from '../../assets/images/electroz_english_primary.svg';

const NAV_LINKS = [
  { label: 'Services',     href: '/#services' },
  { label: 'How It Works', href: '/#how-it-works' },
  { label: 'Why Us',       href: '/#why-us' },
];

export function Navbar() {
  const [open, setOpen]       = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  const isHome = pathname === '/';
  const { data } = useSiteContent();
  const contactInfo = data?.contactInfo;

  useEffect(() => {
    const check = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', check, { passive: true });
    check();
    return () => window.removeEventListener('scroll', check);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname]);

  return (
    <>
      {/* ── Info strip ── collapses when scrolled ── */}
      <div
        className="hidden overflow-hidden transition-all duration-300 md:block"
        style={{
          maxHeight: scrolled ? 0 : '2.5rem',
          background: '#07101e',
          borderBottom: scrolled ? 'none' : '1px solid rgba(255,255,255,0.06)',
        }}
      >
        <div className="mx-auto flex h-10 max-w-6xl items-center justify-between px-6">
          <div className="flex items-center gap-5 text-[12px] text-slate-400">
            {contactInfo?.phone && (
              <a href={`tel:${contactInfo.phone}`} className="flex items-center gap-1.5 transition-colors hover:text-white">
                <Icon name="phone" size={12} className="text-brand-400" />
                {contactInfo.phone}
              </a>
            )}
            {contactInfo?.phone && contactInfo?.email && <span className="h-3 w-px bg-white/10" />}
            {contactInfo?.email && (
              <a href={`mailto:${contactInfo.email}`} className="flex items-center gap-1.5 transition-colors hover:text-white">
                <Icon name="mail" size={12} className="text-brand-400" />
                {contactInfo.email}
              </a>
            )}
          </div>

          <div className="flex items-center gap-2 text-[12px] font-medium text-amber-400">
            <span className="relative flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-400 opacity-60" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-amber-400" />
            </span>
            Available 24/7 for emergencies
          </div>
        </div>
      </div>

      {/* ── Main navbar — always solid white ── */}
      <header className="sticky top-0 z-40 border-b border-slate-200 bg-white shadow-[0_2px_12px_-2px_rgba(16,27,45,0.08)] backdrop-blur-md">
        <div className="mx-auto flex h-[56px] max-w-6xl items-center justify-between px-4 sm:px-6">

          {/* ── Logo ── */}
          <Link to="/" className="group">
            <img src={logoLight} alt="Electroz" className="h-8 w-auto transition-opacity duration-200 group-hover:opacity-80" />
          </Link>

          {/* ── Desktop nav ── */}
          {isHome && (
            <nav className="hidden items-center md:flex">
              {NAV_LINKS.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  className="group relative px-4 py-2 text-[13px] font-medium text-slate-600 transition-colors hover:text-navy-900"
                >
                  {link.label}
                  <span className="absolute bottom-0 left-4 right-4 h-0.5 origin-center scale-x-0 rounded-full bg-brand-600 transition-transform duration-200 group-hover:scale-x-100" />
                </a>
              ))}
            </nav>
          )}

          {/* ── Right actions ── */}
          <div className="flex items-center gap-2.5">
            {/* Phone chip */}
            {isHome && (
              <a
                href={`tel:${contactInfo?.phone ?? ''}`}
                className="hidden items-center gap-1.5 rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-2 text-[13px] font-medium text-slate-700 transition-all hover:border-brand-300 hover:bg-brand-50 hover:text-brand-700 lg:inline-flex"
              >
                <Icon name="phone" size={14} className="text-brand-600" />
                {contactInfo?.phone}
              </a>
            )}


            {/* Hamburger */}
            {isHome && (
              <button
                onClick={() => setOpen(v => !v)}
                aria-label={open ? 'Close menu' : 'Open menu'}
                className="flex h-9 w-9 items-center justify-center rounded-lg text-slate-700 transition-colors hover:bg-slate-100 md:hidden"
              >
                <span className="flex w-[18px] flex-col gap-[5px]">
                  <span className={cn('block h-0.5 rounded-full bg-current transition-all duration-200', open && 'translate-y-[7px] rotate-45')} />
                  <span className={cn('block h-0.5 rounded-full bg-current transition-all duration-200', open && 'opacity-0 scale-x-0')} />
                  <span className={cn('block h-0.5 rounded-full bg-current transition-all duration-200', open && '-translate-y-[7px] -rotate-45')} />
                </span>
              </button>
            )}
          </div>
        </div>

        {/* ── Mobile drawer ── */}
        {isHome && (
          <div
            className={cn(
              'overflow-hidden border-t transition-all duration-300 ease-in-out md:hidden',
              open ? 'max-h-[440px] border-slate-200' : 'max-h-0 border-transparent',
            )}
          >
            <nav className="bg-white px-4 pb-5 pt-3">
              <div className="mb-3 flex flex-wrap items-center gap-3 rounded-xl bg-slate-50 px-4 py-3">
                {contactInfo?.phone && (
                  <a href={`tel:${contactInfo.phone}`} className="flex items-center gap-1.5 text-[12px] font-medium text-slate-700">
                    <Icon name="phone" size={13} className="text-brand-600" /> {contactInfo.phone}
                  </a>
                )}
                {contactInfo?.phone && contactInfo?.email && <span className="h-3 w-px bg-slate-200" />}
                {contactInfo?.email && (
                  <a href={`mailto:${contactInfo.email}`} className="flex items-center gap-1.5 text-[12px] font-medium text-slate-700">
                    <Icon name="mail" size={13} className="text-brand-600" /> {contactInfo.email}
                  </a>
                )}
              </div>

              {NAV_LINKS.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={() => setOpen(false)}
                  className="flex items-center justify-between rounded-xl px-3 py-3 text-[14px] font-medium text-slate-700 transition-colors hover:bg-brand-50 hover:text-brand-700"
                >
                  {link.label}
                  <Icon name="chevronRight" size={15} className="text-slate-300" />
                </a>
              ))}

              <a
                href={`tel:${contactInfo?.phone ?? ''}`}
                className="mt-3 flex items-center justify-center gap-2 rounded-xl bg-brand-600 py-3.5 text-[14px] font-semibold text-white shadow-[0_2px_12px_rgba(23,105,224,0.35)] transition-all hover:bg-brand-700"
              >
                <Icon name="phone" size={15} /> Call Us Now
              </a>
            </nav>
          </div>
        )}
      </header>
    </>
  );
}
